// src/screens/RestAreas.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
  Chip,
  Checkbox,
  FormControlLabel,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Grid,
  Divider,
  Avatar,
  Tooltip,
  InputAdornment,
  Badge,
  Alert,
  styled,
  alpha,
  CircularProgress,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowBack,
  Circle,
  Euro,
  Edit,
  Close,
  LocalParking,
  CheckCircle,
  Wifi,
  WaterDrop,
  Bolt,
  Bathtub,
  LocalLaundryService,
  Restaurant,
  LocalGroceryStore,
  Pets,
  ChildCare,
  Park,
  Stop,
} from '@mui/icons-material';
import axios from 'axios';
import { useForm, Controller } from 'react-hook-form';

const StyledStatus = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
}));

const StatusIndicator = ({ isOccupied, nextAvailableTime, occupiedBy }) => {
  const isActive = isOccupied === false;
  
  const getStatusText = () => {
    if (!isOccupied) return 'Libera';
    
    if (nextAvailableTime) {
      const availableDate = new Date(nextAvailableTime * 1000);
      const now = new Date();
      
      // Se è oggi
      if (availableDate.toDateString() === now.toDateString()) {
        return `Libera alle ${availableDate.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
      }
      
      // Se è domani
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      if (availableDate.toDateString() === tomorrow.toDateString()) {
        return `Libera domani alle ${availableDate.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
      }
      
      // Altrimenti mostra data completa
      return `Libera il ${availableDate.toLocaleDateString('it-IT')} alle ${availableDate.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    return occupiedBy === 'pending' ? 'Pagamento in corso' : 'Occupata';
  };

  return (
    <StyledStatus>
      <Circle
        style={{
          color: isActive ? '#4caf50' : occupiedBy === 'pending' ? '#ff9800' : '#f44336',
          fontSize: '1rem',
        }}
      />
      <Typography 
        variant="body2" 
        fontWeight={500} 
        color={isActive ? 'success.main' : occupiedBy === 'pending' ? 'warning.main' : 'error.main'}
        sx={{ fontSize: '0.75rem' }}
      >
        {getStatusText()}
      </Typography>
    </StyledStatus>
  );
};

const StyledCard = styled(Card)(({ theme, isOccupied }) => ({
  borderRadius: 12,
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 20px rgba(0, 0, 0, 0.1)',
  },
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '4px',
    backgroundColor: isOccupied ? theme.palette.error.main : theme.palette.success.main,
  },
}));

const ServiceChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.2),
  },
}));

// Map service IDs to icons and labels
const serviceIcons = {
  1: { icon: <Wifi fontSize="small" />, label: "Wi-Fi" },
  2: { icon: <WaterDrop fontSize="small" />, label: "Acqua" },
  3: { icon: <Bolt fontSize="small" />, label: "Elettricità" },
  4: { icon: <Bathtub fontSize="small" />, label: "Bagni" },
  5: { icon: <LocalLaundryService fontSize="small" />, label: "Lavanderia" },
  6: { icon: <Restaurant fontSize="small" />, label: "Ristorante" },
  7: { icon: <LocalGroceryStore fontSize="small" />, label: "Minimarket" },
  8: { icon: <Pets fontSize="small" />, label: "Animali" },
  9: { icon: <ChildCare fontSize="small" />, label: "Area bambini" },
  10: { icon: <Park fontSize="small" />, label: "Area verde" },
};

const RestAreas = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [restareas, setRestAreas] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [currentRestArea, setCurrentRestArea] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [terminateLoading, setTerminateLoading] = useState(null);
  const { control, handleSubmit, reset, setValue, getValues } = useForm();
  const apiUrl = process.env.REACT_APP_API_URL;

  const fetchRestAreas = async () => {
    setLoading(true);
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(
        `${apiUrl}/admins/stopzones/${id}/restareas`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setRestAreas(response.data);
    } catch (error) {
      console.error('Error fetching restareas:', error);
      setError('Si è verificato un errore nel caricamento delle piazzole');
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${apiUrl}/admins/services`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  useEffect(() => {
    fetchRestAreas();
    fetchServices();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpen = (restarea = null) => {
    setCurrentRestArea(restarea);
    if (restarea) {
      reset({
        friendlyName: restarea.friendlyName,
        price: restarea.price,
        services: restarea.restareas_services.map((rs) => rs.servicesId),
      });
    } else {
      reset({
        friendlyName: '',
        price: '',
        services: [],
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    // Delay clearing the state until after the modal is closed
    setTimeout(() => {
      setCurrentRestArea(null);
      reset();
    }, 100);
  };

  const handleSave = async (data) => {
    setLoading(true);
    setError('');
    const token = localStorage.getItem('adminToken');
    try {
      const payload = {
        friendlyName: data.friendlyName,
        price: parseFloat(data.price),
        services: data.services || [],
      };
      if (currentRestArea) {
        await axios.put(
          `${apiUrl}/admins/stopzones/${id}/restareas/${currentRestArea.id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        await axios.post(
          `${apiUrl}/admins/stopzones/${id}/restareas`,
          {
            ...payload,
            stopzonesId: id,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }
      fetchRestAreas();
      handleClose();
    } catch (error) {
      console.error('Error saving restarea:', error);
      setError('Si è verificato un errore durante il salvataggio');
    } finally {
      setLoading(false);
    }
  };

  // Get service name by ID
  const getServiceName = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    return service ? service.name : 'Servizio';
  };

  // Get service icon by ID
  const getServiceIcon = (serviceId) => {
    return serviceIcons[serviceId]?.icon || <CheckCircle fontSize="small" />;
  };

  // Terminate current booking or cancel pending order for a restarea
  const handleTerminateBooking = async (restarea) => {
    const actionText = restarea.occupiedBy === 'booking' ? 'terminare la prenotazione' : 'annullare l\'ordine pending';
    if (!window.confirm(`Sei sicuro di voler ${actionText} per ${restarea.friendlyName}?`)) {
      return;
    }

    setTerminateLoading(restarea.id);
    const token = localStorage.getItem('adminToken');
    
    try {
      if (restarea.occupiedBy === 'booking') {
        // Handle active booking termination
        console.log('🔍 Cercando prenotazione attiva per:', restarea.friendlyName);
        
        const response = await axios.get(`${apiUrl}/admins/stops`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        const now = Math.floor(Date.now() / 1000);
        const activeStop = response.data.find(stop => {
          const isForThisRestarea = stop.restareasId === restarea.id;
          const isActive = stop.start < now && stop.end > now;
          return isForThisRestarea && isActive;
        });
        
        if (!activeStop) {
          setError('Nessuna prenotazione attiva trovata per questa piazzola');
          return;
        }

        console.log('✅ Prenotazione attiva trovata:', activeStop.id);

        const terminateResponse = await axios.post(
          `${apiUrl}/admins/stops/terminate`,
          { stopId: activeStop.id },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const { usedDays, originalDays, freedDays } = terminateResponse.data;
        alert(`Prenotazione terminata con successo!\n\nGiorni utilizzati: ${usedDays}\nGiorni originali: ${originalDays}\nGiorni liberati: ${freedDays}`);
        
      } else if (restarea.occupiedBy === 'pending') {
        // Handle pending order cancellation
        console.log('🔍 Cercando ordine pending per:', restarea.friendlyName);
        
        const response = await axios.get(`${apiUrl}/admins/restareas`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        const restareaWithOrders = response.data.find(r => r.id === restarea.id);
        if (!restareaWithOrders || !restareaWithOrders.orders) {
          setError('Nessun ordine pending trovato per questa piazzola');
          return;
        }
        
        const now = Math.floor(Date.now() / 1000);
        const pendingOrder = restareaWithOrders.orders.find(order => 
          order.status === 'pending' && order.start < now && order.end > now
        );
        
        if (!pendingOrder) {
          setError('Nessun ordine pending attivo trovato per questa piazzola');
          return;
        }

        console.log('✅ Ordine pending trovato:', pendingOrder.id);

        const cancelResponse = await axios.post(
          `${apiUrl}/admins/stops/terminate`,
          { orderId: pendingOrder.id },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        alert(`Ordine pending annullato con successo per ${restarea.friendlyName}!`);
      }
      
      // Refresh the rest areas list to update the status
      fetchRestAreas();
      
    } catch (error) {
      console.error('Error in terminate operation:', error);
      setError(error.response?.data?.error || 'Errore durante l\'operazione');
    } finally {
      setTerminateLoading(null);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Tooltip title="Torna alle aree di sosta">
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
        </Tooltip>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 500 }}>
          Piazzole per l'Area di Sosta
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="body1" color="text.secondary">
          Gestisci le piazzole disponibili per questa area di sosta
        </Typography>
        
        <Button
          variant="contained"
          color="primary"
          startIcon={<LocalParking />}
          onClick={() => handleOpen()}
        >
          Aggiungi Piazzola
        </Button>
      </Box>

      {restareas.length > 0 ? (
        <Grid container spacing={3}>
          {restareas.map((restarea) => {
            const sortedServices = restarea.restareas_services
              .map((rs) => rs.servicesId)
              .filter(id => id)
              .sort();
              
            return (
              <Grid item xs={12} md={6} lg={4} key={restarea.id}>
                <StyledCard elevation={3} isOccupied={restarea.isOccupied}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6" fontWeight={600}>
                        {restarea.friendlyName}
                      </Typography>
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={
                          <Avatar sx={{ 
                            width: 22, 
                            height: 22, 
                            bgcolor: restarea.isOccupied ? 'error.main' : 'success.main',
                            border: '2px solid white'
                          }}>
                            {restarea.isOccupied ? '✕' : '✓'}
                          </Avatar>
                        }
                      >
                        <Avatar sx={{ 
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1), 
                          color: 'primary.main',
                          width: 48,
                          height: 48
                        }}>
                          <LocalParking />
                        </Avatar>
                      </Badge>
                    </Box>

                    <Box display="flex" alignItems="center" mb={2}>
                      <Euro fontSize="small" color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6" color="primary.main" fontWeight={600}>
                        {restarea.price} €
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        / giorno
                      </Typography>
                    </Box>

                    <StatusIndicator 
                      isOccupied={restarea.isOccupied} 
                      nextAvailableTime={restarea.nextAvailableTime}
                      occupiedBy={restarea.occupiedBy}
                    />

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                      Servizi disponibili:
                    </Typography>

                    <Box sx={{ minHeight: 80 }}>
                      {sortedServices.length > 0 ? (
                        sortedServices.map((serviceId) => (
                          <ServiceChip
                            key={serviceId}
                            icon={getServiceIcon(serviceId)}
                            label={serviceIcons[serviceId]?.label || getServiceName(serviceId)}
                            size="small"
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Nessun servizio abilitato per questa piazzola
                        </Typography>
                      )}
                    </Box>
                  </CardContent>

                  <CardActions sx={{ pb: 2, px: 2 }}>
                    {restarea.isOccupied ? (
                      <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => handleOpen(restarea)}
                          startIcon={<Edit />}
                          sx={{ flex: 1 }}
                        >
                          Modifica
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          onClick={() => handleTerminateBooking(restarea)}
                          startIcon={terminateLoading === restarea.id ? <CircularProgress size={16} color="inherit" /> : <Stop />}
                          disabled={terminateLoading === restarea.id}
                          sx={{ flex: 1 }}
                        >
                          {terminateLoading === restarea.id ? 'Terminando...' : 'Termina Ora'}
                        </Button>
                      </Box>
                    ) : (
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => handleOpen(restarea)}
                        startIcon={<Edit />}
                        fullWidth
                      >
                        Modifica
                      </Button>
                    )}
                  </CardActions>
                </StyledCard>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Nessuna piazzola trovata
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Inizia aggiungendo una nuova piazzola a quest'area di sosta
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<LocalParking />}
            onClick={() => handleOpen()}
          >
            Aggiungi Piazzola
          </Button>
        </Paper>
      )}

      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {currentRestArea ? 'Modifica Piazzola' : 'Aggiungi Piazzola'}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent>
          <Box component="form" onSubmit={handleSubmit(handleSave)} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="friendlyName"
                  control={control}
                  rules={{ required: 'Il nome è obbligatorio' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Nome Piazzola"
                      fullWidth
                      margin="dense"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocalParking color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="price"
                  control={control}
                  rules={{ 
                    required: 'Il prezzo è obbligatorio',
                    min: { value: 0.01, message: 'Il prezzo deve essere maggiore di zero' }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Prezzo (€ / giorno)"
                      fullWidth
                      margin="dense"
                      type="number"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Euro color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
              Servizi abilitati:
            </Typography>

            <Grid container spacing={1}>
              {services.map((service) => (
                <Grid item xs={12} sm={6} md={4} key={service.id}>
                  <FormControlLabel
                    control={
                      <Controller
                        name="services"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            checked={getValues('services')?.includes(service.id) || false}
                            onChange={(e) => {
                              const currentValues = getValues('services') || [];
                              const newValue = e.target.checked
                                ? [...currentValues, service.id]
                                : currentValues.filter((id) => id !== service.id);
                              setValue('services', newValue);
                            }}
                            color="primary"
                            icon={<Circle />}
                            checkedIcon={<CheckCircle />}
                          />
                        )}
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center">
                        {getServiceIcon(service.id)}
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {service.name}
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} color="inherit">
            Annulla
          </Button>
          <Button 
            onClick={handleSubmit(handleSave)} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? 'Salvataggio...' : (currentRestArea ? 'Aggiorna' : 'Salva')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RestAreas;